import {KeyboardEvent, useCallback} from "react"
import {canvasMoveService} from "../../services/canvasMoveService"
import {canvasContentService} from "../../services/canvasContentService"
import {processArrowKey} from "../utils/processKeyboardArrowKeys"
import {DragState} from "../../model/DragState"
import {clipboardService, isModPressed} from "@dv5/components"
import {useCanvasSettings} from "./useCanvasSettings"

export interface UseCanvasKeyboardHandlersParams {
    canvasId: string
    canvasContent: { widgets: Array<any> }
    canvasSelection: Array<string>
    onChangeSelection: (selection: Array<string>) => void
    editingWidgetId: string | undefined
    dragState: DragState
    setDragState: (dragState: DragState) => void
    onRequestDelete?: (widgetIds: Array<string>) => void
}

export interface UseCanvasKeyboardHandlersReturn {
    handleKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void
}

export const useCanvasKeyboardHandlers = ({
                                              canvasId,
                                              canvasContent,
                                              canvasSelection,
                                              editingWidgetId,
                                              dragState,
                                              setDragState,
                                              onRequestDelete,
                                          }: UseCanvasKeyboardHandlersParams): UseCanvasKeyboardHandlersReturn => {
    const settings = useCanvasSettings(canvasId)
    const alignToGrid = settings.alignToGrid
    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        if (editingWidgetId) {
            return
        }

        if (e.key === 'Escape' && dragState.selectionRect) {
            e.preventDefault()
            setDragState({...dragState, selectionRect: null})
            return
        }

        if (e.key === 'Escape' && dragState.linkCreation) {
            e.preventDefault()
            setDragState({...dragState, linkCreation: null})
            return
        }

        if (e.key === 'Escape' && dragState.selectedLinkIds.length > 0) {
            e.preventDefault()
            setDragState({...dragState, selectedLinkIds: []})
            return
        }

        if (settings.readOnly && (e.key === 'Delete' || e.key.startsWith('Arrow') || (isModPressed(e) && ['d', 'v', 'x'].includes(e.key.toLowerCase())))) {
            e.preventDefault()
            return
        }

        if (e.key === 'Delete' && (dragState.selectedLinkIds.length > 0 || canvasSelection.length > 0)) {
            e.preventDefault()
            if (dragState.selectedLinkIds.length > 0) {
                canvasContentService.deleteWidgetLinks(canvasId, dragState.selectedLinkIds)
                setDragState({...dragState, selectedLinkIds: []})
            }
            if (canvasSelection.length > 0) {
                if (onRequestDelete) {
                    onRequestDelete(canvasSelection)
                } else {
                    canvasContentService.deleteWidgets(canvasId, canvasSelection)
                }
            }
            return
        }

        if (e.key === 'd' && isModPressed(e) && canvasSelection.length > 0) {
            e.preventDefault()
            canvasContentService.duplicateWidgets(canvasId, canvasSelection, canvasContent.widgets, alignToGrid)
            return
        }

        if (e.key === 'c' && isModPressed(e) && canvasSelection.length > 0) {
            e.preventDefault()
            const copyText = canvasContentService.copyWidgets(canvasSelection, canvasContent.widgets)
            clipboardService.copyTo(copyText, true)
            return
        }

        if (canvasSelection.length === 0) {
            return
        }

        const handled = processArrowKey({
            canvasId,
            widgets: canvasContent.widgets,
            canvasSelection,
            key: e.key,
            shiftKey: e.shiftKey,
            alignToGrid,
            moveWidgets: canvasMoveService.moveWidgets,
        })

        if (handled) {
            e.preventDefault()
        }
    }, [canvasId, canvasContent.widgets, canvasSelection, editingWidgetId, dragState, setDragState, onRequestDelete, alignToGrid, settings.readOnly])

    return {
        handleKeyDown,
    }
}
